import { motion } from 'framer-motion';
import { ReactNode } from 'react';
import logoLuminjo from '@/assets/logo-luminjo-blanc.png';
import logoSymbole from '@/assets/logo-luminjo-symbole.png';

interface AuthLayoutProps {
  children: ReactNode;
  tagline?: string;
  subtitle?: string;
  /** Width ratio for the left panel: "half" (1/2), "wide" (2/5) */
  leftPanelWidth?: 'half' | 'wide';
}

export const AuthLayout = ({ 
  children, 
  tagline = "Service client simple et intuitif",
  subtitle,
  leftPanelWidth = 'half'
}: AuthLayoutProps) => {
  const leftWidth = leftPanelWidth === 'half' ? 'lg:w-1/2' : 'lg:w-2/5';
  const rightWidth = leftPanelWidth === 'half' ? 'lg:w-1/2' : 'lg:w-3/5';
  const minHeight = leftPanelWidth === 'half' ? 'min-h-[200px]' : 'min-h-[180px]';

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left side - Logo on dark background */}
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className={`${leftWidth} bg-gradient-to-br from-slate-900 via-slate-800 to-primary/30 flex flex-col items-center justify-center p-8 lg:p-16 ${minHeight} lg:min-h-screen gap-6`}
      >
        <motion.img 
          src={logoSymbole} 
          alt="Luminjo symbole" 
          className={leftPanelWidth === 'half' ? "w-24 lg:w-32 xl:w-40" : "w-16 lg:w-24"}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        />
        <motion.img 
          src={logoLuminjo} 
          alt="Luminjo" 
          className={leftPanelWidth === 'half' ? "w-48 lg:w-72 xl:w-80" : "w-36 lg:w-56"}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        />
        <motion.p
          className="text-white/80 text-lg lg:text-xl font-light tracking-wide text-center"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {tagline}
        </motion.p>
        {subtitle && (
          <motion.p
            className="text-white/60 text-sm mt-2 hidden lg:block"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            {subtitle}
          </motion.p>
        )}
      </motion.div>

      {/* Right side - Content */}
      <motion.div 
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className={`${rightWidth} flex items-center justify-center p-8 lg:p-16 bg-background`}
      >
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {children}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};
